import { Injectable } from '@angular/core';
import { PaymentCycle, SyncApi, SyncOperation } from '@app/core';
import { ContractTerminationType, OrderSyncSchedule, PhoneNumber } from '@app/orders-base';
import { ProductType } from '@app/products-base';
import { GenericService, Utils } from '@messaia/cdk';
import { Observable } from 'rxjs';
import { Order } from '../models/order';

@Injectable({ providedIn: 'root' })
export class OrderService extends GenericService<Order> {

	/**
	 * Constructor
	 */
	constructor() {
		super('order');
	}

	/**
	 * Maps a tariff tp order object.
	 * @param orderId the order tariff
	 */
	public mapTariff(orderId: number): Observable<Order> {
		return this.get(`mapTariff/${orderId}`, null, { 'Disable-Cache': 'Yes' });
	}

	/**
	 * Gets possibile tariff changes
	 * @param orderId the order ID
	 */
	public getPossibleTariffChanges(orderId: number): Observable<any> {
		return this.get(`possibleTariffChanges/${orderId}`, null, { 'Disable-Cache': 'Yes' });
	}

	/**
	 * Prepares tariff change
	 * @param id the order ID
	 * @param tariffChangeId the tariff change ID
	 */
	public prepareTariffChange(orderid: number, tariffChangeId: number): Observable<any> {
		return this.get(`prepareTariffChange/order/${orderid}/tariffChange/${tariffChangeId}`, null, { 'Disable-Cache': 'Yes' });
	}

	/**
	 * Gets available options
	 * @param orderId The ID of the order
	 */
	public getOptions(orderId: number, productType?: ProductType): Observable<any> {
		return this.get(`availabeOptions/${orderId}`, { productType: productType }, { 'Disable-Cache': 'Yes' });
	}

	/**
	 * Gets speed reduction product
	 * @param orderId The ID of the order 
	 */
	public getSpeedReductionProduct(orderId: number): Observable<any> {
		return this.get(`speedReductionProduct/${orderId}`);
	}

	/**
	 * Gets assigned phone numbers
	 * @param orderId The ID of the order
	 */
	public getAssignedPhoneNumbers(orderId: number): Observable<any> {
		return this.get(`assignedPhoneNumbers/${orderId}`, null, { 'Disable-Cache': 'Yes' });
	}

	/**
	 * Deletes a phone number
	 * @param orderId The ID of order
	 * @param phoneNumberId The ID of the phone number
	 */
	public deletePhoneNumber(orderId: number, phoneNumberId: number): Observable<any> {
		return this.update(`deletePhoneNumber/${orderId}/${phoneNumberId}`, null);
	}

	/**
	 * Deletes an imported phone number
	 * @param orderId The ID of order
	 * @param phoneNumberId The ID of the phone number
	 */
	public deleteImportedPhoneNumber(orderId: number, phoneNumberId: number): Observable<any> {
		return this.update(`deleteImportedPhoneNumber/${orderId}/${phoneNumberId}`, null);
	}

	/**
	 * Returns a phone number
	 * @param orderId The ID of order
	 * @param phoneNumberId The ID of the phone number
	 */
	public returnPhoneNumbers(orderId: number, phoneNumberIds: number[]): Observable<any> {
		return this.update(`returnPhoneNumbers/${orderId}`, null, null, { params: Utils.toHttpParams({ phoneNumberIds: phoneNumberIds }) });
	}

	/**
	 * Exports phone numbers
	 * @param orderId The ID of order
	 * @param phoneNumberIds The IDs of the phone numbers to export
	 * @param destinationCarrierId The ID of the dest carrier
	 */
	public exportPhoneNumbers(orderId: number, destinationCarrierId: string, phoneNumberIds: number[]): Observable<any> {
		return this.update(`exportPhoneNumbers/${orderId}`, { destinationCarrierId: destinationCarrierId, phoneNumberIds: phoneNumberIds });
	}

	/**
	 * Orders a new option
	 * @param orderId The ID order to add the bundle to
	 * @param bundleId The ID of the bundle to order
	 * @param quantity The quantity to order
	 */
	public orderOption(orderId: number, bundleId: number, quantity: number = 1): Observable<any> {
		return this.update(`orderOption/${orderId}/bundle/${bundleId}/quantity/${quantity}`, null);
	}

	/**
	 * Schedule a sync for the order with the specified ID
	 * @param orderId 
	 * @param operation 
	 */
	public scheduleSync(orderId: number, operation: SyncOperation) {
		return this.update(`scheduleSync/${orderId}/${operation}`, null);
	}

	/**
	 * Schedule a sync for the order with the specified ID
	 * @param orderIds
	 * @param operation 
	 */
	public scheduleListSync(orderIds: number[], operation: SyncOperation) {
		return this.update(`scheduleSyncList`, null, null, { params: Utils.toHttpParams({ ids: orderIds, operation: operation }) });
	}

	/**
	 * Resets a sync schedule
	 * @param orderId 
	 * @param syncSchedule 
	 * @returns 
	 */
	public updateSyncSchedule(orderId: number, syncSchedule: OrderSyncSchedule): Observable<any> {
		return this.update(`updateSyncSchedule/${orderId}`, syncSchedule);
	}

	/**
	 * Prepares termination for the specified order
	 * @param orderId 
	 * @param orderItemId
	 * @param type 
	 * @returns 
	 */
	public prepareTermination(orderId: number, orderItemId: number, type: ContractTerminationType): Observable<any> {
		return this.get(`prepareTermination`, { orderId: orderId, orderItemId: orderItemId, type: type }, { 'Disable-Cache': 'Yes' });
	}

	/**
	 * Revokes termination of the specified contract
	 * @param orderId The ID of the contract
	 */
	public prepareTerminationRevocation(orderId: number): Observable<any> {
		return this.get(`prepareTerminationRevocation/${orderId}`, null, { 'Disable-Cache': 'Yes' });
	}

	/**
	 * Suggests phone number blocks
	 * @param syncApi 
	 * @param clientId 
	 * @returns 
	 */
	public suggestPhoneNumberBlocks(syncApi: SyncApi, clientId: number): Observable<any> {
		return this.get(`suggestPhoneNumberBlocks`, { syncApi: syncApi, clientId: clientId }, { 'Disable-Cache': 'Yes' });
	}

	/**
	 * Order additional phone numbers
	 * @param orderId The ID of the order item
	 */
	public addPhoneNumbers(orderId: number, phoneNumbers: PhoneNumber[]): Observable<any> {
		return this.update(`addPhoneNumbers/${orderId}`, phoneNumbers);
	}

	/**
	 * Order additional phone number blocks
	 * @param orderId The ID of the order item
	 */
	public addPhoneNumberblocks(orderId: number, count: number): Observable<any> {
		return this.update(`addPhoneNumberblocks/${orderId}`, count);
	}

	/**
	 * Deletes a phone book entry
	 * @param orderId The ID of order
	 * @param phoneBookEntryId The ID of the phone number
	 */
	public deletePhoneBookEntry(orderId: number, phoneBookEntryId: number): Observable<any> {
		return this.update(`deletePhoneBookEntry/${orderId}/phoneBookEntry/${phoneBookEntryId}`, null);
	}

	/**
	 * Gets voice operation status
	 * @param orderId The ID of the order
	 */
	public updateVoiceOperationStatus(orderId: number): Observable<any> {
		return this.update(`updateVoiceOperationStatus/${orderId}`, null);
	}

	/**
	 * Calc totals
	 */
	public calcTotals(order: Order): void {
		if (!order) {
			return;
		}

		/* Get one off & monthly bundles */
		var oneOffBundles = order.bundles?.filter(x => x.paymentCycle == PaymentCycle.OneOff);
		var monthlyBundles = order.bundles?.filter(x => x.paymentCycle == PaymentCycle.Monthly);

		/* Calculate one off totals */
		order.oneOffTotal.net = (oneOffBundles?.reduce((a, b) => a + b.regularSubtotal.net, 0) + order.bundles?.reduce((a, b) => a + b.shippingCostSubtotal.net, 0) || 0).round(4);
		order.oneOffTotal.gross = (oneOffBundles?.reduce((a, b) => a + b.regularSubtotal.gross, 0) + order.bundles?.reduce((a, b) => a + b.shippingCostSubtotal.gross, 0) || 0).round(4);
		order.oneOffTotal.taxAmount = (oneOffBundles?.reduce((a, b) => a + b.regularSubtotal.taxAmount, 0) + order.bundles?.reduce((a, b) => a + b.shippingCostSubtotal.taxAmount, 0) || 0).round(4);

		/* Calculate monthly regular totals */
		order.regularMonthlyTotal.net = (monthlyBundles?.reduce((a, b) => a + b.regularSubtotal.net, 0) || 0).round(4);
		order.regularMonthlyTotal.gross = (monthlyBundles?.reduce((a, b) => a + b.regularSubtotal.gross, 0) || 0).round(4);
		order.regularMonthlyTotal.taxAmount = (monthlyBundles?.reduce((a, b) => a + b.regularSubtotal.taxAmount, 0) || 0).round(4);

		/* Calculate reduced monthly regular totals */
		order.reducedMonthlyTotal.net = (monthlyBundles?.reduce((a, b) => a + b.reducedSubtotal.net, 0) || 0).round(4);
		order.reducedMonthlyTotal.gross = (monthlyBundles?.reduce((a, b) => a + b.reducedSubtotal.gross, 0) || 0).round(4);
		order.reducedMonthlyTotal.taxAmount = (monthlyBundles?.reduce((a, b) => a + b.reducedSubtotal.taxAmount, 0) || 0).round(4);

		/* Calculate shipping costs totals */
		order.shippingCostTotal.net = (order.bundles?.reduce((a, b) => a + b.shippingCostSubtotal.net, 0) || 0).round(4);
		order.shippingCostTotal.gross = (order.bundles?.reduce((a, b) => a + b.shippingCostSubtotal.gross, 0) || 0).round(4);
		order.shippingCostTotal.taxAmount = (order.bundles?.reduce((a, b) => a + b.shippingCostSubtotal.taxAmount, 0) || 0).round(4);
	}
}